import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  BookOpen, 
  ExternalLink, 
  Star, 
  Heart, 
  Sparkles, 
  CheckCircle2, 
  Share2, 
  ShoppingBag,
  Users,
  Feather,
  Smile,
  Quote,
  X
} from 'lucide-react';
const bookCoverImg = "/uploads/a_boy_and_his_brother.png";

interface BooksShowcaseProps {
  onContactClick?: () => void;
}

export function BooksShowcase({ onContactClick }: BooksShowcaseProps) {
  const [isCopied, setIsCopied] = useState(false);
  const [coverHover, setCoverHover] = useState(false);
  const [isZoomOpen, setIsZoomOpen] = useState(false);

  const amazonUrl = "https://www.amazon.com/dp/B0H73PXHB3?lv";

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "A Boy and His Brother by Randon Dean Brown",
          text: "A boy and his brother share a bond like no other. Check out this heartwarming children's book!",
          url: amazonUrl,
        });
      } catch (err) {
        copyToClipboard();
      }
    } else {
      copyToClipboard();
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(amazonUrl);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2500);
  };

  return (
    <div className="space-y-12 max-w-6xl mx-auto">
      {/* ----------------- TOP HEADER BADGE ----------------- */}
      <div className="text-center max-w-3xl mx-auto space-y-4">
        <motion.div 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="inline-flex items-center gap-2 py-1.5 px-4 rounded-full bg-gradient-to-r from-amber-500/15 via-brand-500/20 to-amber-500/15 border border-amber-500/30 text-amber-300 text-xs font-bold uppercase tracking-wider select-none shadow-[0_0_20px_rgba(245,158,11,0.15)]"
        >
          <BookOpen className="w-3.5 h-3.5 text-brand-400" />
          <span>Published Children's Literature</span>
          <span className="w-1.5 h-1.5 rounded-full bg-brand-400 animate-ping" />
        </motion.div>

        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-display font-black text-white tracking-tight">
          A Boy and His <span className="bg-gradient-to-r from-amber-300 via-brand-400 to-amber-500 bg-clip-text text-transparent">Brother</span>
        </h1>

        <p className="text-base sm:text-lg text-amber-200/90 font-medium italic">
          "A boy and his brother share a bond like no other."
        </p>

        <p className="text-xs sm:text-sm text-slate-400 font-mono">
          Written &amp; Illustrated by <span className="text-white font-semibold">Randon Dean Brown</span>
        </p>
      </div>

      {/* ----------------- HERO BOOK PRESENTATION ----------------- */}
      <div className="relative rounded-3xl bg-neutral-950/90 border border-brand-500/30 p-6 sm:p-8 lg:p-10 shadow-[0_0_50px_rgba(245,158,11,0.08)] overflow-hidden">
        {/* Warm Ambient Glow Backdrops */}
        <div className="absolute -top-24 -left-24 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-brand-500/10 rounded-full blur-3xl pointer-events-none" />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center relative z-10">
          
          {/* LEFT: 3D Realistic Book Cover with Real Uploaded Artwork */}
          <div className="lg:col-span-5 flex flex-col items-center justify-center">
            <motion.div 
              className="relative cursor-pointer select-none group"
              onClick={() => setIsZoomOpen(true)}
              onHoverStart={() => setCoverHover(true)}
              onHoverEnd={() => setCoverHover(false)}
              whileHover={{ scale: 1.03, rotateY: -3, rotateX: 2 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
            >
              {/* Outer Book Glow on Hover */}
              <div className="absolute -inset-2 bg-gradient-to-r from-amber-500/30 via-brand-500/20 to-sky-500/30 rounded-3xl blur-xl opacity-60 group-hover:opacity-100 transition-opacity duration-500" />

              {/* Real Book Cover Container */}
              <div className="relative w-72 sm:w-80 md:w-88 aspect-square rounded-2xl overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.8),0_0_30px_rgba(245,158,11,0.25)] border-2 border-white/20 bg-neutral-900">
                <img
                  src={bookCoverImg}
                  alt="A Boy and His Brother by Randon Dean Brown"
                  className="w-full h-full object-cover object-center select-none"
                  referrerPolicy="no-referrer"
                />

                {/* Realistic Book Spine Highlight & Left Fold Simulation */}
                <div className="absolute left-0 top-0 bottom-0 w-5 bg-gradient-to-r from-black/35 via-white/15 to-transparent pointer-events-none" />
                <div className="absolute left-4 top-0 bottom-0 w-[1px] bg-black/20 pointer-events-none" />

                {/* Subtle Glossy Reflection */}
                <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
              </div>

              {/* Available in Paperback Ribbon */}
              <div className="absolute -top-3 -right-3 bg-gradient-to-r from-amber-500 to-brand-500 text-neutral-950 px-3 py-1 rounded-full text-[10px] font-extrabold shadow-lg flex items-center gap-1 select-none">
                <BookOpen className="w-3 h-3" />
                <span>Available in Paperback</span>
              </div>
            </motion.div>

            {/* Sub-label under cover */}
            <p className="text-[11px] text-slate-400 mt-4 font-mono text-center">
              Available in Paperback on Amazon
            </p>
          </div>

          {/* RIGHT: Book Story, Highlights & Direct Buy Action */}
          <div className="lg:col-span-7 space-y-6">
            
            {/* Review Stars & Badge */}
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-1 text-amber-400">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <span className="text-xs font-bold text-amber-300 bg-amber-500/10 px-2.5 py-0.5 rounded-full border border-amber-500/20">
                5.0 out of 5 Stars
              </span>
              <span className="text-xs text-slate-400">
                Family &amp; Siblinghood Picture Book
              </span>
            </div>

            {/* Main Synopsis Paragraphs (Exact User Brief) */}
            <div className="space-y-4 text-slate-300 text-sm sm:text-base leading-relaxed">
              <p className="font-semibold text-amber-100 text-base sm:text-lg">
                Every day is a new adventure when you have a brother. Written and illustrated by a loving father, <span className="text-brand-300 italic font-bold">A Boy and His Brother</span> is an intimate, beautifully crafted window into the joy of siblinghood.
              </p>
              <p className="text-slate-300">
                This touching children's book pairs simple, rhythmic rhymes with stunning, artistic illustrations transformed directly from real family photographs. Follow two brothers through the simple joys of childhood: playing outside, watching their favorite shows, sticking together through every kind of weather, and building a friendship meant to last a lifetime.
              </p>
              <p className="text-slate-400 text-sm bg-neutral-900/60 p-4 rounded-xl border border-neutral-800">
                ✨ <strong className="text-slate-200">Perfect for:</strong> Parents welcoming a second child, brothers sharing a bedroom, or anyone who wants to remind the little ones in their life just how special family truly is.
              </p>
            </div>

            {/* Amazon Purchase CTA Section */}
            <div className="pt-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <a
                href={amazonUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative inline-flex items-center justify-center gap-3 px-8 py-4 rounded-2xl bg-gradient-to-r from-amber-500 via-brand-500 to-amber-500 hover:from-amber-400 hover:via-brand-400 hover:to-amber-400 text-neutral-950 font-display font-black text-base transition-all duration-300 shadow-[0_0_35px_rgba(245,158,11,0.35)] hover:shadow-[0_0_50px_rgba(245,158,11,0.5)] active:scale-98 cursor-pointer"
              >
                <ShoppingBag className="w-5 h-5 text-neutral-950 group-hover:scale-110 transition-transform" />
                <span>Buy Now on Amazon</span>
                <ExternalLink className="w-4 h-4 text-neutral-950 opacity-80 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </a>

              <button
                onClick={handleShare}
                className="inline-flex items-center justify-center gap-2 px-5 py-4 rounded-2xl bg-neutral-900 hover:bg-neutral-800 text-slate-300 hover:text-white border border-neutral-800 hover:border-brand-500/40 text-sm font-semibold transition-all active:scale-98 cursor-pointer"
              >
                <Share2 className="w-4 h-4 text-brand-400" />
                <span>{isCopied ? "Link Copied!" : "Share Book"}</span>
              </button>
            </div>

            {/* Trust Points */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 pt-2 text-xs text-slate-400">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                <span>Amazon Prime Eligible</span>
              </div>
              <div className="flex items-center gap-2">
                <Heart className="w-4 h-4 text-rose-400 shrink-0" />
                <span>Hand-Crafted Art</span>
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-amber-400 shrink-0" />
                <span>Bedtime Favorite</span>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* ----------------- WHY FAMILIES LOVE THIS BOOK ----------------- */}
      <div className="space-y-6">
        <div className="text-center space-y-2">
          <h2 className="text-2xl sm:text-3xl font-display font-bold text-white">
            Why Families Love <span className="text-brand-400">A Boy and His Brother</span>
          </h2>
          <p className="text-sm text-slate-400 max-w-xl mx-auto">
            Crafted with heart, rhythm, and vibrant watercolor imagery to celebrate brotherhood.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          
          {/* Card 1: Family Photographs to Art */}
          <div className="p-6 rounded-2xl bg-neutral-950/80 border border-brand-500/20 hover:border-brand-500/40 transition-all space-y-3">
            <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-400">
              <Feather className="w-5 h-5" />
            </div>
            <h3 className="text-lg font-display font-bold text-white">Art from Real Family Memories</h3>
            <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
              Every illustration is hand-crafted and transformed directly from real family snapshots into warm, storybook watercolor scenes.
            </p>
          </div>

          {/* Card 2: Rhythmic Rhymes */}
          <div className="p-6 rounded-2xl bg-neutral-950/80 border border-brand-500/20 hover:border-brand-500/40 transition-all space-y-3">
            <div className="w-10 h-10 rounded-xl bg-brand-500/10 border border-brand-500/20 flex items-center justify-center text-brand-400">
              <Smile className="w-5 h-5" />
            </div>
            <h3 className="text-lg font-display font-bold text-white">Engaging Rhythmic Rhymes</h3>
            <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
              Simple, musical verses that children love reciting and parents enjoy reading aloud at bedtime over and over again.
            </p>
          </div>

          {/* Card 3: Lifelong Sibling Bond */}
          <div className="p-6 rounded-2xl bg-neutral-950/80 border border-brand-500/20 hover:border-brand-500/40 transition-all space-y-3">
            <div className="w-10 h-10 rounded-xl bg-sky-500/10 border border-sky-500/20 flex items-center justify-center text-sky-400">
              <Users className="w-5 h-5" />
            </div>
            <h3 className="text-lg font-display font-bold text-white">The Gift of Siblinghood</h3>
            <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
              The ultimate gift for baby showers, new brothers, shared bedrooms, or teaching little ones about loyalty and friendship.
            </p>
          </div>

        </div>
      </div>

      {/* ----------------- AUTHOR INSPIRATION & BACKGROUND ----------------- */}
      <div className="rounded-2xl bg-gradient-to-r from-neutral-950 via-neutral-900 to-neutral-950 border border-neutral-800 p-6 sm:p-8 flex flex-col md:flex-row items-center gap-6 text-left">
        <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-brand-500/10 border border-brand-500/30 flex items-center justify-center text-brand-400 shrink-0 shadow-[0_0_20px_rgba(245,158,11,0.15)]">
          <Quote className="w-8 h-8" />
        </div>

        <div className="space-y-2 flex-1">
          <div className="text-xs font-mono font-bold text-brand-400 uppercase tracking-wider">
            About the Author &amp; Creator
          </div>
          <h3 className="text-xl font-display font-bold text-white">
            Written with Love by Randon Dean Brown
          </h3>
          <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
            Beyond engineering municipal fiber backbones and modern web architectures, Randon created <em>A Boy and His Brother</em> as a heartfelt love letter to fatherhood. Inspired by watching his own boys grow up together, this book captures the pure, everyday magic that makes brotherhood a lifelong treasure.
          </p>
        </div>

        <div className="shrink-0 w-full md:w-auto text-center md:text-right">
          <a
            href={amazonUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-amber-500/15 hover:bg-amber-500 text-amber-300 hover:text-neutral-950 border border-amber-500/30 hover:border-amber-500 font-bold text-xs transition-all cursor-pointer shadow-sm"
          >
            <span>Order Your Copy</span>
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>

      {/* ----------------- FULL RESOLUTION ZOOM MODAL ----------------- */}
      <AnimatePresence>
        {isZoomOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsZoomOpen(false)}
            className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md flex items-center justify-center p-4 sm:p-6 cursor-zoom-out"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-w-2xl w-full bg-neutral-950 rounded-3xl border border-brand-500/30 p-3 sm:p-4 shadow-2xl flex flex-col items-center"
            >
              <button
                onClick={() => setIsZoomOpen(false)}
                className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-neutral-900/80 hover:bg-neutral-800 text-white flex items-center justify-center border border-white/20 transition-colors cursor-pointer"
                title="Close"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="w-full aspect-square rounded-2xl overflow-hidden shadow-2xl border border-white/10">
                <img
                  src={bookCoverImg}
                  alt="A Boy and His Brother - Full Cover Art"
                  className="w-full h-full object-contain bg-neutral-900 select-none"
                  referrerPolicy="no-referrer"
                />
              </div>

              <div className="w-full pt-4 px-2 flex items-center justify-between text-xs text-slate-400">
                <span className="font-display font-bold text-white">A Boy and His Brother</span>
                <span>By Randon Dean Brown</span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
